import { Story } from '../../types/story';

export const osloBathhouse: Story = {
  id: 'oslo-bathhouse',
  title: 'New Year, New Waters',
  excerpt: 'A New Year\'s Day tradition at Oslo\'s public bathhouse leads to an unexpected encounter in the cold waters.',
  city: 'Oslo',
  publishDate: '2026-01-01',
  readTime: '9 min read',
  tags: ['♂♂', 'bathhouse', 'winter', 'tradition'],
  categories: ['queer'],
  intensity: 1,
  content: `The Norwegians have a tradition: on New Year's Day, you jump into freezing water to wash away the old year and start fresh. It's supposed to be cleansing, invigorating, a way to prove you're tougher than the winter.

I thought it was insane.

But my friend Kristian insisted. "You're in Oslo for New Year's. You have to do the polar plunge. It's tradition."

"Tradition is drinking champagne and sleeping until noon."

"That's your tradition. Ours is better."

Which is how I found myself at Sørenga Sjøbad at 10 AM on January 1st, hungover and questioning all my life choices, wearing nothing but swim trunks and regret.

The bathhouse was crowded despite the cold—or maybe because of it. Norwegians are a different breed. They were laughing, joking, jumping into the Oslo Fjord like it was a summer day instead of barely above freezing.

"You'll feel amazing after," Kristian promised. "Trust me."

I didn't trust him. But I'd come this far.

I was standing at the edge, trying to work up the courage, when someone spoke behind me.

"First time?"

I turned. He was maybe thirty, dark hair wet from the water, skin flushed from the cold. Norwegian, obviously, with that casual comfort in the cold that locals have.

"That obvious?"

"You've been standing there for five minutes. Either you're contemplating the meaning of life or you're terrified."

"Both."

He laughed. "I'm Anders."

"James."

"English?"

"American. Here visiting a friend."

"And he convinced you to do this?" Anders gestured at the water.

"Against my better judgment."

"It's not as bad as you think. The first ten seconds are terrible. Then your body goes numb and it's fine."

"That's not reassuring."

"Come on. I'll go with you. On three."

"I don't—"

"One."

"Wait—"

"Two."

"I'm not ready—"

"Three!"

He grabbed my hand and we jumped together, hitting the water in a shock of cold that drove every thought from my mind. It was worse than I'd imagined—like being stabbed by a thousand ice needles, like my lungs had forgotten how to work, like every nerve ending was screaming.

And then, just as Anders had promised, the numbness came. My body adjusted, or gave up, or decided this was just life now.

We surfaced, both gasping, and Anders was grinning like a madman.

"See? Not so bad!"

"You're insane. This entire country is insane."

"But you feel alive, yes?"

He was right. Despite the cold, despite the hangover, I felt more awake than I had in months. The water was clear, the sky was bright, and Anders was still holding my hand.

We swam for maybe two minutes before the cold became too much. Back on the dock, wrapped in towels, teeth chattering, I'd never felt more exhilarated.

"You did it," Anders said. "Now you're a real Norwegian."

"I don't think one polar plunge makes me Norwegian."

"It's a start. Come on, let's get coffee. There's a place nearby that does the best kanelboller."

The café was warm, crowded with other polar plungers, everyone pink-cheeked and laughing. Anders bought us coffee and cinnamon buns, and we sat by the window, watching more brave souls jump into the fjord.

"So," he said. "What brings you to Oslo for New Year's?"

"Kristian. We went to university together. He moved back here after graduation, keeps trying to convince me Norway is paradise."

"And what do you think?"

"I think it's cold and dark and everyone is suspiciously attractive."

He smiled. "Suspiciously?"

"It's unsettling. In America, we have a normal distribution of attractiveness. Here, everyone looks like they're in a Viking tourism ad."

"I'll take that as a compliment."

"You should."

We talked for an hour, easy conversation that flowed like we'd known each other longer than thirty minutes. He was a teacher, lived in Grünerløkka, spent his weekends hiking or skiing or doing other impossibly Nordic things.

"How long are you here?" he asked.

"I fly back tomorrow."

"That's a shame."

"Is it?"

"I was going to ask if you wanted to see more of Oslo. The real Oslo, not just the tourist version."

"I'd like that. But I don't have much time."

"Then we'll have to make the most of today." He stood, offered his hand. "Come on. I'll show you my city."

We spent the day walking through Oslo—Vigeland Park, the Opera House, the neighborhoods Kristian had never taken me to. Anders was a good guide, telling stories about the city's history, pointing out hidden details I'd have missed.

As the sun set (early, because Norway in winter), we ended up at his apartment in Grünerløkka. It was small, cozy, filled with books and plants and the kind of comfortable minimalism that Scandinavians do so well.

"I should probably get back," I said, not moving from his couch.

"You should," he agreed, sitting next to me. "But you're not going to."

"No?"

"No. Because I'm going to kiss you, and then you're going to stay."

"That's presumptuous."

"Am I wrong?"

He wasn't wrong.

The kiss was soft at first, tentative, both of us testing the waters like we had that morning in the fjord. But unlike the fjord, this warmth was welcome. We moved together slowly, carefully, learning each other.

We didn't have sex—it felt too fast, too much for one day. But we lay together on his couch, kissing and talking and watching the snow start to fall outside his window.

"I wish you weren't leaving tomorrow," he said.

"Me too."

"Come back. In the summer. Oslo is beautiful in the summer. We have midnight sun, warm weather, everyone goes a little crazy."

"I'll think about it."

"Don't think. Just come."

I left his apartment at midnight, walking back to Kristian's place through quiet streets, snow falling softly. My flight was in twelve hours. I should have been sad, or regretful, or something.

Instead, I felt hopeful.

I did come back, six months later. Anders met me at the airport with flowers and that same grin he'd had when we jumped into the fjord. We spent two weeks exploring Norway—Oslo, Bergen, the fjords, the midnight sun.

And the next New Year's Day, we did the polar plunge together again. It was still terrible. But this time, I had someone to hold my hand.

Sometimes the best things come from the worst ideas. Sometimes jumping into freezing water leads to jumping into something else entirely. Sometimes a New Year's tradition becomes the start of a new life.

Godt nytt år.`
};
